<template>
  <el-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    :title="editData ? '자재 수정' : '자재 추가'"
    width="700px"
    destroy-on-close
  >
    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-width="100px"
      v-loading="loading"
    >
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="자재유형" prop="material_type">
            <el-select v-model="form.material_type" placeholder="자재유형 선택">
              <el-option label="원자재" value="RAW" />
              <el-option label="부자재" value="ACCESSORY" />
              <el-option label="포장재" value="PACKING" />
              <el-option label="기타" value="OTHER" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="구매유형" prop="purchase_type">
            <el-select v-model="form.purchase_type" placeholder="구매유형 선택">
              <el-option label="시장사입" value="MARKET" />
              <el-option label="거래처구매" value="SUPPLIER" />
              <el-option label="재고사용" value="STOCK" />
              <el-option label="브랜드제공" value="BRAND" />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="자재명" prop="material_name">
            <el-input v-model="form.material_name" placeholder="자재명 입력" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="규격">
            <el-input v-model="form.specification" placeholder="예: 150cm x 100m" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="색상">
            <el-input v-model="form.color" placeholder="색상 입력" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="공급처">
            <el-select v-model="form.supplier" placeholder="공급처 선택" clearable filterable>
              <el-option
                v-for="supplier in suppliers"
                :key="supplier.supplier_id"
                :label="supplier.name"
                :value="supplier.supplier_id"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="8">
          <el-form-item label="수량" prop="quantity">
            <el-input-number v-model="form.quantity" :min="0" :precision="2" style="width: 100%;" />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="단위" prop="unit">
            <el-select v-model="form.unit" placeholder="단위 선택">
              <el-option label="M" value="M" />
              <el-option label="YD" value="YD" />
              <el-option label="EA" value="EA" />
              <el-option label="KG" value="KG" />
              <el-option label="SET" value="SET" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="단가" prop="unit_price">
            <el-input-number
              v-model="form.unit_price"
              :min="0"
              :precision="0"
              :step="100"
              style="width: 100%;"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="구매처">
            <el-input v-model="form.purchase_location" placeholder="구매 장소 입력" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="합계금액">
            <el-input :value="formatCurrency(form.quantity * form.unit_price)" disabled />
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item label="메모">
        <el-input
          v-model="form.memo"
          type="textarea"
          :rows="2"
          placeholder="메모 입력"
        />
      </el-form-item>

      <!-- 부위별 소요량 (견적 산출용) -->
      <el-divider content-position="left">
        부위별 소요량
        <span class="sub-hint">견적 자재 산출의 기준 — 부위별로 입력 (선택)</span>
      </el-divider>
      <el-table :data="usages" size="small" border style="width: 100%">
        <el-table-column label="사용부위" min-width="140">
          <template #default="{ row }">
            <el-input v-model="row.part" size="small" placeholder="예: 몸판, 소매, 카라" />
          </template>
        </el-table-column>
        <el-table-column label="소요량" width="130">
          <template #default="{ row }">
            <el-input-number v-model="row.usage_quantity" :min="0" :precision="4" :step="0.1" size="small" style="width: 100%" />
          </template>
        </el-table-column>
        <el-table-column label="단위" width="90">
          <template #default="{ row }">
            <el-select v-model="row.unit" size="small" style="width: 100%">
              <el-option label="YD" value="YD" />
              <el-option label="M" value="M" />
              <el-option label="EA" value="EA" />
              <el-option label="KG" value="KG" />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column label="" width="56" align="center">
          <template #default="{ $index }">
            <el-button type="danger" size="small" text @click="usages.splice($index, 1)">삭제</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-button size="small" style="margin-top: 6px" @click="usages.push({ part: '', usage_quantity: 0, unit: form.unit || 'YD', memo: '' })">
        + 부위 추가
      </el-button>
    </el-form>

    <template #footer>
      <el-button @click="$emit('update:modelValue', false)">취소</el-button>
      <el-button type="primary" @click="handleSubmit" :loading="submitting">
        {{ editData ? '수정' : '추가' }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, watch } from 'vue'
import { ElMessage } from 'element-plus'
import axiosInstance from '@/api/axios'

const props = defineProps({
  modelValue: Boolean,
  sampleRequestId: { type: [String, Number], default: null },
  sampleRoundId: { type: [String, Number], default: null },
  editData: { type: Object, default: null }, // 수정 모드 (부위별 소요량 편집)
})

const emit = defineEmits(['update:modelValue', 'saved'])

const formRef = ref(null)
const loading = ref(false)
const submitting = ref(false)
const suppliers = ref([])
const usages = ref([]) // 부위별 소요량 행

const form = reactive({
  material_type: null,
  purchase_type: 'MARKET',
  material_name: '',
  specification: '',
  color: '',
  supplier: null,
  quantity: 0,
  unit: 'M',
  unit_price: 0,
  purchase_location: '',
  memo: ''
})

const rules = {
  material_type: [{ required: true, message: '자재유형을 선택하세요', trigger: 'change' }],
  purchase_type: [{ required: true, message: '구매유형을 선택하세요', trigger: 'change' }],
  material_name: [{ required: true, message: '자재명을 입력하세요', trigger: 'blur' }],
  quantity: [{ required: true, message: '수량을 입력하세요', trigger: 'blur' }],
  unit: [{ required: true, message: '단위를 선택하세요', trigger: 'change' }]
}

const formatCurrency = (amount) => {
  if (!amount) return '0원'
  return Number(amount).toLocaleString() + '원'
}

const fetchSuppliers = async () => {
  try {
    const response = await axiosInstance.get('suppliers/')
    suppliers.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch suppliers:', error)
  }
}

const resetForm = () => {
  form.material_type = null
  form.purchase_type = 'MARKET'
  form.material_name = ''
  form.specification = ''
  form.color = ''
  form.supplier = null
  form.quantity = 0
  form.unit = 'M'
  form.unit_price = 0
  form.purchase_location = ''
  form.memo = ''
}

const handleSubmit = async () => {
  if (!formRef.value) return

  try {
    await formRef.value.validate()
    submitting.value = true

    const data = {
      ...form,
      sample_request: props.sampleRequestId,
      ...(props.sampleRoundId ? { sample_round: props.sampleRoundId } : {})
    }

    let materialId
    if (props.editData?.id) {
      const res = await axiosInstance.put(`sample-materials/${props.editData.id}/`, data)
      materialId = res.data.id
    } else {
      const res = await axiosInstance.post('sample-materials/', data)
      materialId = res.data.id
    }

    // 부위별 소요량 전체 교체 저장
    const validUsages = usages.value.filter(u => (u.part || '').trim())
    await axiosInstance.post(`sample-materials/${materialId}/save_usages/`, { usages: validUsages })

    ElMessage.success(props.editData ? '자재가 수정되었습니다.' : '자재가 추가되었습니다.')
    emit('update:modelValue', false)
    emit('saved')
  } catch (error) {
    if (error !== 'cancel' && error !== false) {
      console.error('Submit failed:', error)
      ElMessage.error(error.response?.data?.detail || '저장 중 오류가 발생했습니다.')
    }
  } finally {
    submitting.value = false
  }
}

watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    resetForm()
    usages.value = []
    fetchSuppliers()
    if (props.editData) {
      Object.assign(form, props.editData)
      usages.value = (props.editData.usages || []).map(u => ({
        part: u.part, usage_quantity: Number(u.usage_quantity), unit: u.unit || 'YD', memo: u.memo || '',
      }))
    }
  }
})
</script>

<style scoped>
.el-select {
  width: 100%;
}
.sub-hint { font-size: 11px; color: #909399; font-weight: 400; margin-left: 8px; }
</style>
